import { useMemo } from 'react';

import { analyser, type Bloc, type Cellule, type Inline } from './lib/markdown';

/**
 * Repris de la console SuperAlfred (components/Markdown.tsx) : il y rendait les messages des agents,
 * il rend ici les notes du cerveau.
 *
 * Rend du Markdown en éléments React.
 *
 * Aucun HTML n'est produit à aucun moment : le texte vient d'un agent, et
 * React échappe tout ce qu'il affiche. Il n'y a donc rien à assainir — la
 * seule voie qui subsisterait est l'URL d'un lien, fermée à l'analyse.
 */

function rendreInlines(noeuds: Inline[]): React.ReactNode {
  return noeuds.map((n, i) => {
    switch (n.type) {
      case 'texte':
        return <span key={i}>{n.texte}</span>;
      case 'gras':
        return <strong key={i}>{rendreInlines(n.enfants)}</strong>;
      case 'italique':
        return <em key={i}>{rendreInlines(n.enfants)}</em>;
      case 'barre':
        return <del key={i}>{rendreInlines(n.enfants)}</del>;
      case 'code':
        return (
          <code key={i} className="md-code">
            {n.texte}
          </code>
        );
      case 'lien':
        // Un lien interne (un [[wikilien]] vers une autre note) reste dans l'app.
        if (n.href.startsWith('#')) {
          return <a key={i} href={n.href} className="md-interne">{rendreInlines(n.enfants)}</a>;
        }
        return (
          // `noopener` : sans lui, la page ouverte peut manipuler celle-ci.
          <a key={i} href={n.href} target="_blank" rel="noreferrer noopener">
            {rendreInlines(n.enfants)}
          </a>
        );
      case 'saut':
        return <br key={i} />;
    }
  });
}

function rendreBlocs(blocs: Bloc[]): React.ReactNode {
  return blocs.map((b, i) => {
    switch (b.type) {
      case 'paragraphe':
        return <p key={i}>{rendreInlines(b.enfants)}</p>;
      case 'titre': {
        // Les niveaux de titre d'un message ne sont pas ceux du document :
        // on garde la hiérarchie visuelle sans fausser la structure de la page.
        const Balise = `h${Math.min(6, b.niveau + 2)}` as 'h3';
        return <Balise key={i}>{rendreInlines(b.enfants)}</Balise>;
      }
      case 'code':
        return (
          <pre key={i} className="md-pre">
            {b.langue && <span className="md-langue">{b.langue}</span>}
            <code>{b.texte}</code>
          </pre>
        );
      case 'liste': {
        const items = b.items.map((contenu, j) => <li key={j}>{rendreBlocs(contenu)}</li>);
        return b.ordonnee ? <ol key={i}>{items}</ol> : <ul key={i}>{items}</ul>;
      }
      case 'citation':
        return (
          <blockquote key={i} className="md-cite">
            {rendreBlocs(b.blocs)}
          </blockquote>
        );
      case 'tableau': {
        const style = (c: Cellule): React.CSSProperties =>
          c.alignement ? { textAlign: c.alignement } : {};
        return (
          // Enveloppé : un tableau large doit défiler dans son cadre, jamais
          // élargir la bulle ni la page — c'est sur téléphone que cela compte.
          <div key={i} className="md-tableau">
            <table>
              <thead>
                <tr>
                  {b.entetes.map((c, j) => (
                    <th key={j} style={style(c)}>
                      {rendreInlines(c.enfants)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.lignes.map((ligne, j) => (
                  <tr key={j}>
                    {ligne.map((c, k) => (
                      <td key={k} style={style(c)}>
                        {rendreInlines(c.enfants)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      case 'separateur':
        return <hr key={i} className="md-hr" />;
    }
  });
}

export function Markdown({ texte }: { texte: string }): React.ReactElement {
  // L'analyse est refaite à chaque rendu sans cela, et la conversation se
  // réaffiche à chaque battement d'horloge de la console.
  const blocs = useMemo(() => analyser(texte), [texte]);
  return <div className="md">{rendreBlocs(blocs)}</div>;
}
